/**
 * Categorize a coverage task label into "where did this assignment come
 * from" so the UI can show meaningful prefixes instead of every item
 * saying "Coverage". Source of truth for the mapping rules so home card
 * + Zone Sheet chips + manager feed all read the same way.
 *
 * User ask 2026-02-15: "ACO should display as: Zone - ACOs · Emails
 * should display as: Win the Day - Emails · T-Shirt Wall should
 * display as: Task - T-Shirt Wall".
 */

const RULES = [
  // Zone Recovery / store-coverage assignments
  { test: /^aco(s)?$|cashier|adopt.a/i,         prefix: "Zone",        display: "ACOs" },
  { test: /^front(\s|$)|register|cash\s*wrap/i, prefix: "Zone",        display: null },
  // Win-the-Day rotating focuses
  { test: /email|capture/i,                     prefix: "Win the Day", display: null },
  { test: /bopis/i,                             prefix: "Win the Day", display: null },
  { test: /balloon/i,                           prefix: "Win the Day", display: null },
  { test: /recover/i,                           prefix: "Win the Day", display: null },
  { test: /go.?back/i,                          prefix: "Win the Day", display: null },
  // Catch-all
];

/**
 * Categorize by both `kind` (from the API) and the raw label. Kind
 * wins when it's a known bucket — Win the Day rows shouldn't need
 * their label to include a keyword like "balloon" or "email" to be
 * prefixed correctly.
 */
export function categorizeTask({ kind = "", label = "" } = {}) {
  const k = String(kind || "").toLowerCase();
  if (k === "win_the_day") {
    return { prefix: "Win the Day", display: label || "", full: `Win the Day - ${label || ""}` };
  }
  if (k === "zone") {
    return { prefix: "Zone", display: label || "", full: `Zone - ${label || ""}` };
  }
  if (k === "opening") {
    return { prefix: "Opening", display: label || "", full: `Opening - ${label || ""}` };
  }
  if (k === "closing") {
    return { prefix: "Closing", display: label || "", full: `Closing - ${label || ""}` };
  }
  return categorizeTaskLabel(label);
}

export function categorizeTaskLabel(label) {
  if (!label) return { prefix: "Task", display: "", full: "Task" };
  const clean = String(label).trim();
  for (const r of RULES) {
    if (r.test.test(clean)) {
      const disp = r.display || clean;
      return { prefix: r.prefix, display: disp, full: `${r.prefix} - ${disp}` };
    }
  }
  return { prefix: "Task", display: clean, full: `Task - ${clean}` };
}

export const STATUS_META = {
  not_acknowledged: { label: "Not Seen",                    short: "Not Seen",       dot: "bg-slate-400",   chip: "bg-slate-100 text-slate-700 border-slate-200" },
  acknowledged:     { label: "Acknowledged",                short: "Ack’d",          dot: "bg-sky-500",     chip: "bg-sky-100 text-sky-700 border-sky-200" },
  in_progress:      { label: "In Progress",                 short: "In Progress",    dot: "bg-indigo-500",  chip: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  completed:        { label: "Waiting for Mgr Verification",short: "Awaiting Verify",dot: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  manager_verified: { label: "Verified",                    short: "Verified",       dot: "bg-emerald-700", chip: "bg-emerald-600 text-white border-emerald-700" },
  needs_attention:  { label: "Needs Attention",             short: "Needs Attention",dot: "bg-rose-500",    chip: "bg-rose-100 text-rose-700 border-rose-200" },
  needs_handoff:    { label: "Needs Handoff",               short: "Needs Handoff",  dot: "bg-amber-500",   chip: "bg-amber-100 text-amber-700 border-amber-300" },
};

/**
 * Decide what attribution byline to render under a completed/verified
 * task — the user wants to be able to glance at a task and know who
 * closed it without opening a dialog.
 *
 *   Status              | Returned byline
 *   -------------------+----------------------------------
 *   completed           | "Awaiting verify" (no signer yet)
 *   manager_verified +
 *     completed_by ==
 *     verified_by AND
 *     completed_by is
 *     a manager         | "Manager Signed Off by Dom"
 *   manager_verified
 *     otherwise         | "Verified by Matthias"
 *
 * Returns null if there's nothing useful to render (e.g. status is
 * "in_progress" or the row is missing both names).
 * (User ask 2026-06-13 — Task Manager attribution.)
 */
export function taskAttributionLine(task) {
  if (!task) return null;
  const status = task.status;
  if (status === "completed") {
    return "Awaiting manager verify";
  }
  if (status !== "manager_verified") return null;
  const verifier = task.verified_by_name || "";
  const completer = task.completed_by_name || "";
  const sameSigner =
    task.verified_by && task.completed_by && task.verified_by === task.completed_by;
  if (sameSigner && task.completed_by_is_manager) {
    return verifier ? `Manager Signed Off by ${verifier}` : "Manager Signed Off";
  }
  if (verifier) return `Verified by ${verifier}`;
  if (completer) return `Completed by ${completer}`;
  return "Verified";
}
